import { ICreditCard, ICustomersGetResponse } from '@/shared/endpoints/customers/models/response';
import { CustomersController } from '@/shared/endpoints/customers';
import { OnboardingController } from '@/shared/endpoints/onboarding';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { onLoadSetupThunk } from '@/routingInitHelpers';
import { produce } from 'immer';
import { checkHasPregnancy } from '@/shared/helpers/main';
import { IMedication, ISelectedMedication } from '@/shared/types/interfaces/medication';
import { IMedicationQuantity } from '@/shared/constants/medication-quantity';
export interface IRegisterData {
    firstName: string;
    lastName: string;
}
export interface IOnboardingState {
    user: ICustomersGetResponse | null;
    userLoading: boolean;
    isIntroductionScreen?: boolean;
    elibigilityFailedPopupOpen: boolean;
    hasPregnancy?: boolean;
    fixedBlock?: HTMLElement;
    insuranceLookupTriggered: boolean;
    isScrollVisible: boolean;
    isOnlyOneMedication: boolean;
    isOpenedHelper: boolean;
    accountStatus: {
        loading: boolean;
        error: Error | null;
        complete: boolean;
    };
    selectedMedication?: ISelectedMedication;
    isPopoverAutocompleteOpen?: boolean;
    registerData: IRegisterData;
}

const defaultAccountStatus = {
    loading: false,
    error: null,
    complete: false
};

export const initialState: IOnboardingState = {
    user: null,
    hasPregnancy: false,
    fixedBlock: undefined,
    insuranceLookupTriggered: false,
    elibigilityFailedPopupOpen: false,
    isScrollVisible: false,
    isOnlyOneMedication: false,
    isOpenedHelper: false,
    accountStatus: { ...defaultAccountStatus },
    selectedMedication: undefined,
    registerData: {
        firstName: '',
        lastName: ''
    }
};

const onboardingSlice = createSlice({
    name: 'onboarding',
    initialState,
    reducers: {
        resetOnboarding: produce((state: IOnboardingState) => {
            state.user = null;
            state.insuranceLookupTriggered = false;
            state.accountStatus = { ...defaultAccountStatus };
        }),
        setEligibilityFailedModalOpen: produce((state: IOnboardingState, action: PayloadAction<boolean>) => {
            state.elibigilityFailedPopupOpen = action.payload;
        }),
        setFixedBlock: produce((state: IOnboardingState, action: PayloadAction<HTMLElement | undefined>) => {
            state.fixedBlock = action.payload;
        }),
        setInsuranceLookupTriggered: produce((state: IOnboardingState, action: PayloadAction<boolean>) => {
            state.insuranceLookupTriggered = action.payload;
        }),
        showScroll: (state, action: PayloadAction<boolean>) => {
            state.isScrollVisible = action.payload;
        },
        setIsOnlyOneMedication: produce((state: IOnboardingState, action: PayloadAction<boolean>) => {
            state.isOnlyOneMedication = action.payload;
        }),
        setIsOpenedHelper: produce((state: IOnboardingState, action: PayloadAction<boolean>) => {
            state.isOpenedHelper = action.payload;
        }),
        updatePreferredCard: produce((state: IOnboardingState, action: PayloadAction<ICreditCard>) => {
            if (state.user) {
                state.user.creditCards = state.user.creditCards.map(card => ({ ...card, preferred: card.id === action.payload.id }));
            }
        }),
        resetOnboardingState: state => {
            Object.entries(initialState).forEach(([key, value]) => {
                state[key as keyof IOnboardingState] = value;
            });
        },
        setSelectedMedication: produce((state: IOnboardingState, action: PayloadAction<IMedication | undefined>) => {
            if (action.payload) state.selectedMedication = { prescribable: action.payload };
            else state.selectedMedication = undefined;
        }),
        setSelectedMedicationQuantity: produce((state: IOnboardingState, action: PayloadAction<IMedicationQuantity | undefined>) => {
            if (state.selectedMedication) {
                state.selectedMedication.quantity = action.payload?.quantity;
                state.selectedMedication.daysSupply = action.payload?.daysSupply;
            }
        }),
        setIsPopoverAutocompleteOpen: produce((state: IOnboardingState, action: PayloadAction<boolean>) => {
            state.isPopoverAutocompleteOpen = action.payload;
        }),
        setRegisterData: (state, action: { payload: IRegisterData }) => {
            state.registerData = action.payload;
        }
    },
    extraReducers: builder => {
        builder
            .addCase(onLoadSetupThunk.pending, state => {
                state.accountStatus.loading = true;
            })
            .addCase(onLoadSetupThunk.rejected, (state, action) => {
                state.accountStatus.loading = false;
                state.accountStatus.error = action.payload!;
            })
            .addCase(onLoadSetupThunk.fulfilled, (state, action) => {
                state.accountStatus.loading = false;
                state.accountStatus.error = null;
                state.accountStatus.complete = true;
                state.user = action.payload;
                state.hasPregnancy = checkHasPregnancy(action.payload.userProfile);
            })
            .addCase(OnboardingController.createUserProfile.fulfilled, (state, action) => {
                state.hasPregnancy = checkHasPregnancy(action.payload);
                state.user = {
                    ...state.user,
                    userProfile: action.payload
                } as any;
            })
            .addCase(OnboardingController.updateUserProfile.fulfilled, (state, action) => {
                if (state.user) {
                    state.user = {
                        ...state.user,
                        userProfile: {
                            ...action.payload
                        }
                    } as any;
                }
                state.hasPregnancy = checkHasPregnancy(action.payload);
            })
            .addCase(OnboardingController.addUserAddress.fulfilled, (state, action) => {
                const payload: any = action.payload;
                const currentUserAddresses = state?.user?.addresses || [];
                if (payload.preferred) {
                    currentUserAddresses.forEach(address => {
                        address.preferred = false;
                    });
                }
                state.user = {
                    ...state.user,
                    addresses: [...currentUserAddresses, payload]
                } as any;
            })
            .addCase(OnboardingController.deleteUserAddress.fulfilled, (state, action) => {
                state.user = {
                    ...state.user,
                    addresses: state?.user?.addresses?.filter(address => address.id !== action?.meta?.arg?.id)
                } as any;
            })
            .addCase(CustomersController.get.pending, state => {
                state.userLoading = true;
            })
            .addCase(CustomersController.get.fulfilled, (state, action) => {
                state.user = action.payload;
                state.userLoading = false;
                state.hasPregnancy = checkHasPregnancy(action.payload.userProfile);
            })
            .addCase(CustomersController.get.rejected, state => {
                state.userLoading = false;
            })
            .addCase(CustomersController.updateMetadata.fulfilled, (state, action) => {
                state.user = { ...state.user, userOnboarding: action.payload };
            })
            .addCase(CustomersController.logout.fulfilled, state => {
                state.user = undefined;
                state.insuranceLookupTriggered = false;
                state.accountStatus = {
                    ...defaultAccountStatus
                };
            })
            .addCase(CustomersController.eligibility.fulfilled, (state, action) => {
                if (state.user)
                    state.user = {
                        ...state.user,
                        insurances: [...action.payload]
                    };
            })
            .addCase(CustomersController.insuranceCreate.fulfilled, (state, action) => {
                if (state.user && !state.user.insurances.some(ins => ins.id === action.payload?.id))
                    state.user = {
                        ...state.user,
                        insurances: [...state.user.insurances, action.payload]
                    };
            })
            .addCase(CustomersController.insuranceUpload.fulfilled, (state, action) => {
                if (state.user) {
                    if (!action.payload.addedInsurance) {
                        state.user = {
                            ...state.user,
                            insuranceUploads: [...state.user.insuranceUploads, action.payload]
                        };

                        return;
                    }

                    const notYetAdded = !state.user.insurances.some(ins => ins.id === action.payload.addedInsurance?.id);

                    if (notYetAdded) {
                        state.user = {
                            ...state.user,
                            insurances: [...state.user.insurances, action.payload.addedInsurance]
                        };
                    }
                }
            })
            .addCase(CustomersController.insuranceDelete.fulfilled, (state, action) => {
                const { id } = action.meta.arg;

                if (state.user)
                    state.user = {
                        ...state.user,
                        insurances: state.user.insurances.filter(insurance => insurance.id !== id)
                    };
            })
            .addCase(CustomersController.insuranceUploadDelete.fulfilled, (state, action) => {
                const { id } = action.meta.arg;

                if (state.user)
                    state.user = {
                        ...state.user,
                        insuranceUploads: state.user.insuranceUploads.filter(insurance => insurance.id !== id)
                    };
            })
            .addCase(CustomersController.addPaymentMethod.fulfilled, (state, action) => {
                if (state.user) {
                    state.user = {
                        ...state.user,
                        creditCards: [...state.user.creditCards, action.payload!]
                    };
                }
            })
            .addCase(CustomersController.paymentMethodDelete.fulfilled, (state, action) => {
                const { id } = action.meta.arg;
                if (state.user)
                    state.user = {
                        ...state.user,
                        creditCards: state.user.creditCards.filter(card => card.id !== id)
                    };
            })
            .addCase(CustomersController.addTransferPrescription.fulfilled, (state, action) => {
                if (state.user && action.payload) state.user.transferPrescriptions.push(action.payload);
            })
            .addCase(CustomersController.removeTransferPrescription.fulfilled, (state, action) => {
                if (state.user) state.user.transferPrescriptions = state.user.transferPrescriptions.filter(p => p.id !== action.payload);
            });
    }
});

export const {
    setFixedBlock,
    setInsuranceLookupTriggered,
    resetOnboarding,
    showScroll,
    setIsOpenedHelper,
    setIsOnlyOneMedication,
    updatePreferredCard,
    resetOnboardingState,
    setSelectedMedication,
    setSelectedMedicationQuantity,
    setIsPopoverAutocompleteOpen,
    setEligibilityFailedModalOpen,
    setRegisterData
} = onboardingSlice.actions;

export default onboardingSlice;
